import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface SelectHeaderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
}

interface SectionProps {
  children?: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled SelectHeader layout component.
 *
 * Provides the header layout structure with left and right sections.
 * Use with sub-components: PaginationInfo, PaginationControls.
 *
 * @example
 * ```tsx
 * <SelectHeader>
 *   <SelectHeader.Left>
 *     <PaginationInfo startItem={1} endItem={50} totalCount={100} />
 *   </SelectHeader.Left>
 *   <SelectHeader.Right>
 *     <PaginationControls hasPrevPage={false} hasNextPage={true} onNextPage={handleNext} />
 *   </SelectHeader.Right>
 * </SelectHeader>
 * ```
 */
const SelectHeader = React.memo(
  ({ children, style, className, ...props }: SelectHeaderProps) => {
    const [css, theme] = useStyletron()

    const headerClassName = css(getHeaderStyles(theme))

    return (
      <div
        className={`${headerClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {children}
      </div>
    )
  }
)

// ═══════════════════════════════════════════════════════════════════════════
// Sub-components for layout
// ═══════════════════════════════════════════════════════════════════════════

const Left = React.memo(({ children }: SectionProps) => {
  const [css] = useStyletron()

  const leftSectionClassName = css(getLeftSectionStyles())

  return <div className={leftSectionClassName}>{children}</div>
})

Left.displayName = 'SelectHeader.Left'

const Right = React.memo(({ children }: SectionProps) => {
  const [css] = useStyletron()

  const rightSectionClassName = css(getRightSectionStyles())

  return <div className={rightSectionClassName}>{children}</div>
})

Right.displayName = 'SelectHeader.Right'

// Attach sub-components
const SelectHeaderWithSections = Object.assign(SelectHeader, {
  Left,
  Right,
})

SelectHeader.displayName = 'SelectHeader'

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

const getHeaderStyles = (theme: any) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid' as const,
  borderBottomColor: theme.colors.grey20,
  backgroundColor: theme.colors.white,
})

const getLeftSectionStyles = () => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const getRightSectionStyles = () => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

export default SelectHeaderWithSections
